import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Store, ChevronLeft, Eye, EyeOff } from 'lucide-react';
import { 
  Input, 
  Button, 
  Select, 
  SelectItem, 
  Checkbox 
} from '@heroui/react';
import { useAuth } from '@/contexts/AuthContext';
import { getPrimaryApiErrorMessage } from '@/utils/errorHandler';
import { cn } from '@/utils/cn';
import { apiService } from '@/services/api';
import { FullScreenSignupLayout } from '@/components/ui/full-screen-signup';
import { useToast } from '@/hooks/useToast';
import { getPasswordStrength, strengthColors, strengthTextColors } from '@/utils/passwordUtils';

enum BusinessType {
  RETAIL = 'RETAIL',
  RESTAURANT = 'RESTAURANT',
}

enum BusinessCategory {
  AFRICAN = 'AFRICAN',
  ASIAN = 'ASIAN',
  GENERAL = 'GENERAL',
}

interface AddressForm {
  street: string;
  city: string;
  postcode: string;
  country: string;
}

interface AdminUserForm {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

interface RegisterBusinessFormData {
  name: string;
  businessType: BusinessType;
  category: BusinessCategory;
  address: AddressForm;
  phone: string;
  email: string;
  vatNumber?: string;
  emailVerificationCode: string;
  adminUser: AdminUserForm;
  planSlug: 'starter' | 'shop_plus' | 'business_hub';
}

const flatInputClassNames = {
  inputWrapper: 'bg-default-100 hover:bg-default-200/70 focus-within:!bg-default-100 rounded-xl shadow-none',
  label: 'text-sm font-semibold text-gray-800',
};

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
const isValidUkPostcode = (postcode: string) => /^[A-Za-z]{1,2}[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}$/.test(postcode.trim());

const RegisterPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<RegisterBusinessFormData>({
    name: '',
    businessType: BusinessType.RETAIL,
    category: BusinessCategory.GENERAL,
    address: { street: '', city: '', postcode: '', country: 'GB' },
    phone: '',
    email: '',
    vatNumber: '',
    emailVerificationCode: '',
    adminUser: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
    },
    planSlug: 'starter',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const sendingRef = useRef(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const totalSteps = 4;

  const handleBusinessChange = (field: string, value: any) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      if (field === 'email' && (prev.adminUser.email === '' || prev.adminUser.email === prev.email)) {
        newData.adminUser = { ...prev.adminUser, email: value };
      }
      return newData;
    });
  };

  const handleAdminChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      adminUser: { ...prev.adminUser, [field]: value },
    }));
  };

  const handleAddressChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return Boolean(formData.name.trim()) && isValidEmail(formData.email);
      case 2:
        return (
          Boolean(formData.address.street.trim()) &&
          Boolean(formData.address.city.trim()) &&
          isValidUkPostcode(formData.address.postcode) &&
          Boolean(formData.address.country.trim())
        );
      case 3:
        return (
          Boolean(formData.adminUser.email.trim()) &&
          formData.adminUser.password.length >= 8 &&
          Boolean(formData.adminUser.firstName.trim()) &&
          Boolean(formData.adminUser.lastName.trim())
        );
      case 4:
        return /^\d{6}$/.test(formData.emailVerificationCode.trim()) && acceptedTerms;
      default:
        return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      await apiService.client.post('/api/auth/verify', {
        email: formData.adminUser.email,
        code: formData.emailVerificationCode.trim(),
      });
      await login({
        email: formData.adminUser.email,
        password: formData.adminUser.password,
      });
    } catch (error: unknown) {
      toast.error(getPrimaryApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      if (currentStep === 4) {
        setFormData((prev) => ({ ...prev, emailVerificationCode: '' }));
      }
      setCurrentStep(currentStep - 1);
    }
  };

  const sendVerificationEmail = async () => {
    setSendingCode(true);
    try {
      await apiService.client.post('/api/auth/register/send-verification-code', formData);
    } catch (err: unknown) {
      toast.error(getPrimaryApiErrorMessage(err));
      throw err;
    } finally {
      setSendingCode(false);
    }
  };

  const goToVerificationStep = async () => {
    if (currentStep !== 3 || !canProceed() || sendingRef.current) return;
    sendingRef.current = true;
    try {
      await sendVerificationEmail();
      setCurrentStep(4);
    } catch {
      // error handled in sendVerificationEmail
    } finally {
      sendingRef.current = false;
    }
  };

  const stepCopy: Record<number, { title: string; subtitle: string }> = {
    1: {
      title: 'Get started',
      subtitle: 'Add your business details — it only takes a minute.',
    },
    2: {
      title: 'Business Address',
      subtitle: 'Where is your store or venue based?',
    },
    3: {
      title: 'Profile',
      subtitle: 'Create your profile and password',
    },
    4: {
      title: 'Verify your email',
      subtitle: 'Enter 6-digit code.',
    },
  };

  return (
    <FullScreenSignupLayout
      leftHeadline=""
      rightTitle={stepCopy[currentStep].title}
      rightSubtitle={stepCopy[currentStep].subtitle}
      icon={<Store className="h-10 w-10" aria-hidden />}
      maxWidth="max-w-xl"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {/* Step 1: Initial business info (reduced churn: just business name and email) */}
        {currentStep === 1 && (
          <div className="flex flex-col gap-4">
            <Input
              label="Business name"
              labelPlacement="outside-top"
              value={formData.name}
              onChange={(e) => handleBusinessChange('name', e.target.value)}
              isRequired
              variant="flat"
              classNames={flatInputClassNames}
            />

            <Input
              label="Email"
              type="email"
              name="email"
              labelPlacement="outside-top"
              value={formData.email}
              onChange={(e) => handleBusinessChange('email', e.target.value)}
              isRequired
              isInvalid={Boolean(formData.email.trim()) && !isValidEmail(formData.email)}
              errorMessage={Boolean(formData.email.trim()) && !isValidEmail(formData.email) ? 'Invalid email address' : undefined}
              variant="flat"
              classNames={flatInputClassNames}
            />
          </div>
        )}

        {/* Step 2: Business Address */}
        {currentStep === 2 && (
          <div className="flex flex-col gap-4">
            <Input
              label="Street address"
              labelPlacement="outside-top"
              value={formData.address.street}
              onChange={(e) => handleAddressChange('street', e.target.value)}
              isRequired
              variant="flat"
              classNames={flatInputClassNames}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="City"
                labelPlacement="outside-top"
                value={formData.address.city}
                onChange={(e) => handleAddressChange('city', e.target.value)}
                isRequired
                variant="flat"
                classNames={flatInputClassNames}
              />

              <Input
                label="Postcode"
                name="postcode"
                labelPlacement="outside-top"
                value={formData.address.postcode}
                onChange={(e) => handleAddressChange('postcode', e.target.value.toUpperCase())}
                isRequired
                variant="flat"
                pattern="^[A-Za-z]{1,2}[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}$"
                isInvalid={Boolean(formData.address.postcode.trim()) && !isValidUkPostcode(formData.address.postcode)}
                errorMessage={Boolean(formData.address.postcode.trim()) && !isValidUkPostcode(formData.address.postcode) ? 'Invalid postcode' : undefined}
                classNames={flatInputClassNames}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Country"
                labelPlacement="outside-top"
                selectedKeys={[formData.address.country]}
                onSelectionChange={(keys) => {
                  const val = Array.from(keys)[0];
                  if (val) handleAddressChange('country', String(val));
                }}
                isRequired
                variant="flat"
                classNames={{
                  trigger: 'bg-default-100 hover:bg-default-200/70 focus-within:!bg-default-100 rounded-xl shadow-none',
                  label: 'text-sm font-semibold text-gray-800',
                }}
              >
                <SelectItem key="GB" textValue="United Kingdom">
                  United Kingdom
                </SelectItem>
              </Select>

              <Input
                label="Phone (optional)"
                type="tel"
                placeholder="+44"
                labelPlacement="outside-top"
                value={formData.phone}
                onChange={(e) => handleBusinessChange('phone', e.target.value)}
                variant="flat"
                classNames={flatInputClassNames}
              />
            </div>
          </div>
        )}

        {/* Step 3: Administrator Account */}
        {currentStep === 3 && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="First name"
                labelPlacement="outside-top"
                value={formData.adminUser.firstName}
                onChange={(e) => handleAdminChange('firstName', e.target.value)}
                isRequired
                variant="flat"
                classNames={flatInputClassNames}
              />

              <Input
                label="Last name"
                labelPlacement="outside-top"
                value={formData.adminUser.lastName}
                onChange={(e) => handleAdminChange('lastName', e.target.value)}
                isRequired
                variant="flat"
                classNames={flatInputClassNames}
              />
            </div>

            <div>
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                labelPlacement="outside-top"
                value={formData.adminUser.password}
                onChange={(e) => handleAdminChange('password', e.target.value)}
                isRequired
                variant="flat"
                endContent={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                }
                classNames={flatInputClassNames}
              />

              {formData.adminUser.password && (
                <div className="space-y-2 mt-2">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map((i) => {
                      const strength = getPasswordStrength(formData.adminUser.password);
                      return (
                        <div
                          key={i}
                          className={cn(
                            'h-1 flex-1 rounded-full transition-colors duration-300',
                            i < strength.score ? strengthColors[strength.score] : 'bg-gray-200'
                          )}
                        />
                      );
                    })}
                  </div>
                  <p className={cn('text-xs font-medium', strengthTextColors[getPasswordStrength(formData.adminUser.password).score])}>
                    {getPasswordStrength(formData.adminUser.password).label}
                  </p>
                  <ul className="space-y-0.5">
                    {getPasswordStrength(formData.adminUser.password).checks.map((check) => (
                      <li key={check.label} className="flex items-center gap-1.5 text-xs">
                        <span className={check.passed ? 'text-emerald-500' : 'text-gray-300'}>
                          {check.passed ? '\u2713' : '\u2717'}
                        </span>
                        <span className={check.passed ? 'text-emerald-700' : 'text-gray-400'}>
                          {check.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

          
          </div>
        )}

        {/* Step 4: Verify email */}
        {currentStep === 4 && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-slate-600">
              We sent a verification code to{' '}
              <span className="font-semibold text-slate-900">{formData.adminUser.email}</span>. Enter it
              below to finish registration.
            </p>

            <Input
              label="6-digit code"
              labelPlacement="outside-top"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={formData.emailVerificationCode}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                setFormData((prev) => ({ ...prev, emailVerificationCode: v }));
              }}
              variant="flat"
              classNames={{
                inputWrapper: 'bg-default-100 hover:bg-default-200/70 focus-within:!bg-default-100 rounded-xl h-14 shadow-none',
                input: 'text-center text-2xl tracking-[0.35em] font-semibold font-mono text-gray-900',
                label: 'text-sm font-semibold text-gray-800',
              }}
            />

            <div className="flex items-center justify-between text-xs">
              <Button
                type="button"
                variant="light"
                size="sm"
                onPress={() => void sendVerificationEmail().catch(() => {})}
                isLoading={sendingCode}
                className="font-semibold text-[#172e50] px-0 h-auto underline underline-offset-2 bg-transparent"
              >
                {sendingCode ? 'Sending…' : 'Resend code'}
              </Button>
            </div>

            <div className="flex items-start gap-2 pt-2">
              <Checkbox
                isSelected={acceptedTerms}
                onValueChange={setAcceptedTerms}
                color="primary"
                classNames={{
                  label: 'text-sm text-slate-600 leading-snug',
                  wrapper: 'before:border-gray-300',
                }}
              >
                <span>
                  I agree to the{' '}
                  <Link to="#" className="font-semibold text-[#172e50] hover:underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="#" className="font-semibold text-[#172e50] hover:underline">
                    Privacy Policy
                  </Link>
                </span>
              </Checkbox>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex items-center gap-3 pt-2">
          {currentStep > 1 && (
            <Button
              type="button"
              variant="ghost"
              isIconOnly
              onPress={prevStep}
              className="shrink-0 rounded-xl"
            >
              <ChevronLeft className="size-5" />
            </Button>
          )}

          {currentStep < totalSteps ? (
            <Button
              type="button"
              color="primary"
              className="flex-1 min-h-12 font-semibold rounded-full bg-[#172e50] text-white shadow-none"
              isLoading={currentStep === 3 && sendingCode}
              isDisabled={!canProceed()}
              onPress={currentStep === 3 ? () => void goToVerificationStep() : nextStep}
            >
              {currentStep === 3 ? 'Send code & continue' : 'Continue'}
            </Button>
          ) : (
            <Button
              type="submit"
              color="primary"
              className="flex-1 min-h-12 font-semibold rounded-full bg-[#172e50] text-white shadow-none"
              isLoading={loading}
              isDisabled={!canProceed()}
            >
              Complete setup
            </Button>
          )}
        </div>
      </form>

      <div className="mt-4 text-center text-sm text-slate-600">
        Already have an account?{' '}
        <Link to="/login" className="font-semibold text-[#172e50] hover:text-[#2d3166] underline underline-offset-2">
          Log in
        </Link>
      </div>
      <div className="mt-3 text-center text-xs text-slate-500">
        <Link to="#" className="font-semibold text-[#172e50] hover:underline no-underline">
          Terms of use
        </Link>
        <span className="mx-2 text-slate-300">·</span>
        <Link to="#" className="font-semibold text-[#172e50] hover:underline no-underline">
          Privacy
        </Link>
      </div>
    </FullScreenSignupLayout>
  );
};

export default RegisterPage;


